import ClearIcon from '@/assets/Weather Icon/Clear.svg';
import CloudyIcon from '@/assets/Weather Icon/cloudy.svg';
import HeavyRainIcon from '@/assets/Weather Icon/heavyrain.svg';
import HeavyThunderstormIcon from '@/assets/Weather Icon/heavythunderstrom.svg';
import LightRainIcon from '@/assets/Weather Icon/lightrain.svg';
import LightThunderstormIcon from '@/assets/Weather Icon/lightthunderstrom.svg';

type WeatherBucket =
  | 'clear'
  | 'cloudy'
  | 'light-rain'
  | 'heavy-rain'
  | 'light-thunderstorm'
  | 'heavy-thunderstorm';

// `icon` is the raw Open-Meteo WMO weather_code (see
// scheduled_jobs/weather/fetch_weather.py WMO_CONDITIONS) collapsed onto the
// 6 buckets the design/translation table provides.
const CODES_BY_BUCKET: Record<WeatherBucket, Set<number>> = {
  clear: new Set([0, 1]),
  cloudy: new Set([2, 3, 45, 48, 71, 73, 75, 77, 85, 86]),
  'light-rain': new Set([51, 53, 55, 56, 57, 61, 66, 80]),
  'heavy-rain': new Set([63, 65, 67, 81, 82]),
  'light-thunderstorm': new Set([95]),
  'heavy-thunderstorm': new Set([96, 99]),
};

const bucketByCode = (code: number): WeatherBucket | null => {
  for (const bucket of Object.keys(CODES_BY_BUCKET) as WeatherBucket[]) {
    if (CODES_BY_BUCKET[bucket].has(code)) return bucket;
  }
  return null;
};

// Fallback for when `icon` is missing but the provider's free-text
// `condition` still is (or a future provider that doesn't use WMO codes).
const bucketByCondition = (condition: string): WeatherBucket | null => {
  const text = condition.toLowerCase();
  if (text.includes('thunder')) {
    return text.includes('heavy') || text.includes('hail')
      ? 'heavy-thunderstorm'
      : 'light-thunderstorm';
  }
  if (text.includes('rain') || text.includes('drizzle') || text.includes('shower')) {
    return text.includes('heavy') || text.includes('violent') || text.includes('moderate')
      ? 'heavy-rain'
      : 'light-rain';
  }
  if (
    text.includes('cloud') ||
    text.includes('overcast') ||
    text.includes('fog') ||
    text.includes('snow')
  ) {
    return 'cloudy';
  }
  if (text.includes('clear')) return 'clear';
  return null;
};

const classifyWeather = (code?: number | null, condition?: string | null): WeatherBucket => {
  if (code != null) {
    const byCode = bucketByCode(code);
    if (byCode) return byCode;
  }
  if (condition) {
    const byCondition = bucketByCondition(condition);
    if (byCondition) return byCondition;
  }
  return 'clear';
};

const ICON_BY_BUCKET: Record<WeatherBucket, string> = {
  clear: ClearIcon,
  cloudy: CloudyIcon,
  'light-rain': LightRainIcon,
  'heavy-rain': HeavyRainIcon,
  'light-thunderstorm': LightThunderstormIcon,
  'heavy-thunderstorm': HeavyThunderstormIcon,
};

// Keys must exist in `public.cm_language` (see postgres/seed/10_smart_city_dashboard_language.sql)
const TRANSLATION_KEY_BY_BUCKET: Record<WeatherBucket, string> = {
  clear: 'cmn>master>clear',
  cloudy: 'cmn>master>cloudy',
  'light-rain': 'cmn>master>light rain',
  'heavy-rain': 'cmn>master>heavy rain',
  'light-thunderstorm': 'cmn>master>light thunderstorm',
  'heavy-thunderstorm': 'cmn>master>heavy thunderstorm',
};

/** Resolve a backend weather reading to one of the bundled Weather Icon assets. */
export const getWeatherIcon = (code?: number | null, condition?: string | null): string =>
  ICON_BY_BUCKET[classifyWeather(code, condition)];

/** Resolve a backend weather reading to a translatable `cmn>master>*` key. */
export const getWeatherConditionKey = (code?: number | null, condition?: string | null): string =>
  TRANSLATION_KEY_BY_BUCKET[classifyWeather(code, condition)];
